import { useEffect, useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../../utils/apiError'
import {
  archiveNotification,
  archiveNotificationsBulk,
  getNotificationSummary,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  unarchiveNotification,
} from '../api/notificationApi'

const DEFAULT_PAGE_SIZE = 8

function toIsoDate(value) {
  return value.toISOString().slice(0, 10)
}

function buildDateRangeFromPreset(preset) {
  if (preset === 'all') return { fromDate: '', toDate: '' }
  const now = new Date()
  const days = preset === '90d' ? 90 : preset === '30d' ? 30 : 7
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const start = new Date(end)
  start.setDate(end.getDate() - (days - 1))
  return { fromDate: toIsoDate(start), toDate: toIsoDate(end) }
}

function buildScopeFlags(scope) {
  if (scope === 'archived') return { includeArchived: true, archivedOnly: true }
  if (scope === 'include_archived') return { includeArchived: true, archivedOnly: false }
  return { includeArchived: false, archivedOnly: false }
}

function uniqueUpper(values) {
  const seen = new Set()
  return (values || [])
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .filter((value) => {
      const key = value.toUpperCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .sort((a, b) => a.localeCompare(b))
}

function toRelativeTime(value) {
  if (!value) return '-'
  const created = new Date(value)
  if (Number.isNaN(created.getTime())) return '-'
  const diffMs = Date.now() - created.getTime()
  const minutes = Math.max(1, Math.floor(diffMs / 60000))
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function useNotificationScreen() {
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [severityFilter, setSeverityFilter] = useState('all')
  const [moduleFilter, setModuleFilter] = useState('all')
  const [datePreset, setDatePreset] = useState('all')
  const [scopeFilter, setScopeFilter] = useState('active')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(DEFAULT_PAGE_SIZE)
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionMessage, setActionMessage] = useState('')
  const [actionBusyId, setActionBusyId] = useState(null)
  const [refreshTick, setRefreshTick] = useState(0)
  const [summary, setSummary] = useState({
    unreadCount: 0,
    archivedCount: 0,
    totalCount: 0,
    severities: [],
    modules: [],
  })
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })

  const dateRange = useMemo(() => buildDateRangeFromPreset(datePreset), [datePreset])
  const scopeFlags = useMemo(() => buildScopeFlags(scopeFilter), [scopeFilter])

  const listParams = useMemo(
    () => ({
      page,
      page_size: pageSize,
      includeTotal: true,
      unreadOnly: statusFilter === 'unread',
      includeArchived: scopeFlags.includeArchived,
      archivedOnly: scopeFlags.archivedOnly,
      severity: severityFilter === 'all' ? '' : severityFilter,
      module: moduleFilter === 'all' ? '' : moduleFilter,
      search: searchText.trim(),
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    }),
    [
      dateRange.fromDate,
      dateRange.toDate,
      moduleFilter,
      page,
      pageSize,
      scopeFlags.archivedOnly,
      scopeFlags.includeArchived,
      searchText,
      severityFilter,
      statusFilter,
    ],
  )

  const summaryParams = useMemo(
    () => ({
      severity: severityFilter === 'all' ? '' : severityFilter,
      module: moduleFilter === 'all' ? '' : moduleFilter,
      search: searchText.trim(),
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    }),
    [dateRange.fromDate, dateRange.toDate, moduleFilter, searchText, severityFilter],
  )

  useEffect(() => {
    let active = true

    async function loadList() {
      setIsLoading(true)
      setError('')
      try {
        const response = await listNotifications(listParams)
        if (!active) return
        const items = Array.isArray(response?.items) ? response.items : []
        setRows(items)
        setMeta({
          total: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load notifications.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadList()
    return () => {
      active = false
    }
  }, [listParams, page, pageSize, refreshTick])

  useEffect(() => {
    let active = true
    async function loadSummary() {
      try {
        const response = await getNotificationSummary(summaryParams)
        if (!active) return
        setSummary({
          unreadCount: Number(response?.unreadCount || 0),
          archivedCount: Number(response?.archivedCount || 0),
          totalCount: Number(response?.totalCount || 0),
          severities: uniqueUpper(response?.severities || []),
          modules: uniqueUpper(response?.modules || []),
        })
      } catch {
        if (!active) return
        setSummary((previous) => ({
          ...previous,
          unreadCount: 0,
          archivedCount: 0,
          totalCount: 0,
        }))
      }
    }
    loadSummary()
    return () => {
      active = false
    }
  }, [summaryParams, refreshTick])

  useEffect(() => {
    function onNotificationsUpdated() {
      setRefreshTick((prev) => prev + 1)
    }
    window.addEventListener('notifications-updated', onNotificationsUpdated)
    return () => {
      window.removeEventListener('notifications-updated', onNotificationsUpdated)
    }
  }, [])

  const severityOptions = useMemo(
    () => [{ value: 'all', label: 'All Priorities' }, ...summary.severities.map((value) => ({ value, label: value }))],
    [summary.severities],
  )

  const moduleOptions = useMemo(
    () => [{ value: 'all', label: 'All Modules' }, ...summary.modules.map((value) => ({ value, label: value }))],
    [summary.modules],
  )

  const formattedRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        relativeTime: toRelativeTime(row.createdAt),
      })),
    [rows],
  )

  const totalPages = useMemo(() => {
    if (!meta.total || !meta.pageSize) return meta.hasMore ? page + 1 : Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!formattedRows.length) return 0
    return (page - 1) * pageSize + 1
  }, [formattedRows.length, page, pageSize])

  const rangeEnd = useMemo(() => {
    if (!formattedRows.length) return 0
    return rangeStart + formattedRows.length - 1
  }, [formattedRows.length, rangeStart])

  function resetPageAndSet(setter, value) {
    setter(value)
    setPage(1)
  }

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setPage(nextPage)
  }

  function goToPreviousPage() {
    setPage((prev) => Math.max(1, prev - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) {
      setPage((prev) => prev + 1)
    }
  }

  async function runAction(action, { successMessage, fallbackMessage, actionId = null } = {}) {
    setActionMessage('')
    setError('')
    setActionBusyId(actionId)
    try {
      const response = await action()
      setActionMessage(successMessage || response?.message || 'Updated successfully.')
      setRefreshTick((prev) => prev + 1)
      window.dispatchEvent(new Event('notifications-updated'))
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, fallbackMessage || 'Action failed.'))
    } finally {
      setActionBusyId(null)
    }
  }

  function currentFilterPayload() {
    return {
      unreadOnly: statusFilter === 'unread',
      includeArchived: scopeFlags.includeArchived,
      archivedOnly: scopeFlags.archivedOnly,
      severity: severityFilter === 'all' ? null : severityFilter,
      module: moduleFilter === 'all' ? null : moduleFilter,
      search: searchText.trim() || null,
      fromDate: dateRange.fromDate || null,
      toDate: dateRange.toDate || null,
    }
  }

  async function markSingleRead(notificationId) {
    setRows((previous) =>
      previous.map((row) =>
        Number(row?.id) === Number(notificationId)
          ? { ...row, isRead: true, readAt: row?.readAt || new Date().toISOString() }
          : row,
      ),
    )
    await runAction(() => markNotificationRead(notificationId), {
      successMessage: 'Notification marked as read.',
      fallbackMessage: 'Failed to mark notification as read.',
      actionId: notificationId,
    })
  }

  async function markFilteredRead() {
    setRows((previous) => previous.map((row) => ({ ...row, isRead: true, readAt: row?.readAt || new Date().toISOString() })))
    await runAction(() => markAllNotificationsRead(currentFilterPayload()), {
      successMessage: 'Filtered notifications marked as read.',
      fallbackMessage: 'Failed to mark notifications as read.',
    })
  }

  async function archiveSingle(notificationId) {
    setRows((previous) => {
      if (scopeFilter === 'active') {
        return previous.filter((row) => Number(row?.id) !== Number(notificationId))
      }
      return previous.map((row) => (Number(row?.id) === Number(notificationId) ? { ...row, isArchive: true } : row))
    })
    await runAction(() => archiveNotification(notificationId), {
      successMessage: 'Notification archived.',
      fallbackMessage: 'Failed to archive notification.',
      actionId: notificationId,
    })
  }

  async function unarchiveSingle(notificationId) {
    setRows((previous) => {
      if (scopeFilter === 'archived') {
        return previous.filter((row) => Number(row?.id) !== Number(notificationId))
      }
      return previous.map((row) => (Number(row?.id) === Number(notificationId) ? { ...row, isArchive: false } : row))
    })
    await runAction(() => unarchiveNotification(notificationId), {
      successMessage: 'Notification unarchived.',
      fallbackMessage: 'Failed to unarchive notification.',
      actionId: notificationId,
    })
  }

  async function archiveFiltered() {
    setRows((previous) => {
      if (scopeFilter === 'active') return []
      return previous.map((row) => ({ ...row, isArchive: true }))
    })
    await runAction(
      () =>
        archiveNotificationsBulk({
          ...currentFilterPayload(),
          archive: true,
        }),
      {
        successMessage: 'Filtered notifications archived.',
        fallbackMessage: 'Failed to archive filtered notifications.',
      },
    )
  }

  return {
    state: {
      searchText,
      statusFilter,
      severityFilter,
      moduleFilter,
      datePreset,
      scopeFilter,
      page,
      totalPages,
      totalCount: meta.total,
      hasMore: meta.hasMore,
      isLoading,
      error,
      actionMessage,
      actionBusyId,
      rows: formattedRows,
      rangeStart,
      rangeEnd,
      summary,
      severityOptions,
      moduleOptions,
    },
    actions: {
      setSearchText: (value) => resetPageAndSet(setSearchText, value),
      setStatusFilter: (value) => resetPageAndSet(setStatusFilter, value),
      setSeverityFilter: (value) => resetPageAndSet(setSeverityFilter, value),
      setModuleFilter: (value) => resetPageAndSet(setModuleFilter, value),
      setDatePreset: (value) => resetPageAndSet(setDatePreset, value),
      setScopeFilter: (value) => resetPageAndSet(setScopeFilter, value),
      goToPage,
      goToPreviousPage,
      goToNextPage,
      markSingleRead,
      markFilteredRead,
      archiveSingle,
      unarchiveSingle,
      archiveFiltered,
    },
  }
}
